import { Route } from "@dashboard/components/Router";
import { parse as parseQs } from "qs";
import React, { useContext } from "react";
import { Switch } from "react-router-dom";

import Layout from "./components/Layout";
import LoginLoading from "./components/LoginLoading";
import { UserContext as Context } from "./types";
import {
  LoginUrlQueryParams,
  newPasswordPath,
  passwordResetPath,
  passwordResetSuccessPath,
} from "./urls";
import LoginViewComponent from "./views/Login";
import NewPassword from "./views/NewPassword";
import ResetPassword from "./views/ResetPassword";
import ResetPasswordSuccess from "./views/ResetPasswordSuccess";
import { useWsmClerkTenant } from "./wsmClerk";

export const UserContext = React.createContext<Context>({
  login: undefined,
  loginByExternalPlugin: undefined,
  logout: undefined,
  signOut: undefined,
  requestLoginByExternalPlugin: undefined,
  authenticating: false,
  isCredentialsLogin: false,
  authenticated: false,
  errors: [],
  loginMessage: null,
  refetchUser: undefined,
});

export const LoginView = () => {
  const qs = parseQs(location.search.substr(1)) as any;
  const params: LoginUrlQueryParams = qs;

  return <LoginViewComponent params={params} />;
};

/**
 * A password page, on the tenants that have a password. On a Web Shop Manager tenant
 * the credential is Clerk's, so "forgot password", the mailed reset link and the
 * set-password page have nothing to act on here: they render the sign-in route, which
 * sends the browser to Clerk's hosted sign-in, where Clerk's own forgotten-password
 * flow lives. Until the tenant has answered, no door, for the same reason as the
 * sign-in route: a password form shown first and swapped out is still a password form
 * on a Web Shop Manager tenant.
 */
const passwordDoor = <P extends object>(Page: React.ComponentType<P>) => {
  const PasswordDoor = (props: P) => {
    const { enabled, loading } = useWsmClerkTenant();

    if (loading) {
      return <LoginLoading />;
    }

    if (enabled) {
      return <LoginViewComponent params={{}} />;
    }

    return <Page {...props} />;
  };

  PasswordDoor.displayName = `PasswordDoor(${Page.displayName ?? Page.name})`;

  return PasswordDoor;
};

export const ResetPasswordRoute = passwordDoor(ResetPassword);
export const ResetPasswordSuccessRoute = passwordDoor(ResetPasswordSuccess);
export const NewPasswordRoute = passwordDoor(NewPassword);

const AuthRouter = () => (
  <Layout>
    <Switch>
      <Route path={passwordResetSuccessPath} component={ResetPasswordSuccessRoute} />
      <Route path={passwordResetPath} component={ResetPasswordRoute} />
      <Route path={newPasswordPath} component={NewPasswordRoute} />
      <Route component={LoginView} />
    </Switch>
  </Layout>
);

AuthRouter.displayName = "AuthRouter";
export default AuthRouter;

export * from "./utils";
export const useUser = () => useContext(UserContext);
